import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { DashboardStatus, Timeline, TimelineBatch, ProcessParameter } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";
import GanttComparisonChart from "../../../components/MES/GanttComparisonChart";
import { buildActualBatches, countBatchesWithActual } from "./actualTimelineUtils";
import { GanttBatchGroupBy } from "../Planning/planningUtils";

/**
 * MES Dashboard - Real-time Production Status
 */
const Dashboard = () => {
  savePathToLocalStorage();

  const [status, setStatus] = useState<DashboardStatus | null>(null);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [processParams, setProcessParams] = useState<ProcessParameter[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate());
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showPlannedGantt, setShowPlannedGantt] = useState(true);
  const [showActualGantt, setShowActualGantt] = useState(true);
  const [ganttGroupBy, setGanttGroupBy] = useState<GanttBatchGroupBy>("mixer");

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
  };

  useEffect(() => {
    loadStatus();
  }, [selectedDate]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh) {
      interval = setInterval(loadStatus, 60000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, selectedDate]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const [data, timelineData, params] = await Promise.all([
        mesApi.getDashboardStatus(selectedDate),
        mesApi.getTimelineByDate(selectedDate),
        mesApi.getProcessParameters({ production_date: selectedDate }),
      ]);
      setStatus(data);
      setTimeline(timelineData?.Status === "confirmed" ? timelineData : null);
      setProcessParams(params);
    } catch (err: any) {
      toast.error("Failed to load dashboard: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const plannedBatches = timeline?.TimelineBatch || [];
  const actualBatches =
    plannedBatches.length > 0
      ? buildActualBatches(plannedBatches, processParams, selectedDate)
      : [];

  const handleStatusChange = async (batch: TimelineBatch, newStatus: string) => {
    try {
      await mesApi.updateBatchStatus(batch.ID, newStatus);
      toast.success(`Status updated: ${batch.MixCode} -> ${newStatus}`);
      loadStatus();
    } catch (err: any) {
      toast.error("Failed to update status: " + err.message);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed": return "Zavrseno";
      case "in_progress": return "U tijeku";
      case "planned": return "Planirano";
      case "ready": return "Spremno";
      case "on_hold": return "Pauzirano";
      case "skipped": return "Preskoceno";
      case "canceled": return "Otkazano";
      default: return status;
    }
  };

  const calculateProgress = () => {
    if (!status || status.totalBatches === 0) return 0;
    return Math.round((status.completed / status.totalBatches) * 100);
  };

  // Calculate product-level progress
  const getProductProgress = () => {
    if (!status?.batches || status.batches.length === 0) return [];

    const productMap: { [key: string]: { product_name: string; total: number; completed: number; in_progress: number; totalMassKg: number } } = {};

    for (const batch of status.batches) {
      const key = batch.ProductName || "Nepoznat";
      if (!productMap[key]) {
        productMap[key] = { product_name: key, total: 0, completed: 0, in_progress: 0, totalMassKg: 0 };
      }
      productMap[key].total += 1;
      productMap[key].totalMassKg += batch.FinalMassKg || 0;
      if (batch.BatchStatus === "completed") {
        productMap[key].completed += 1;
      } else if (batch.BatchStatus === "in_progress") {
        productMap[key].in_progress += 1;
      }
    }

    return Object.values(productMap).sort((a, b) => b.total - a.total);
  };

  const statusOptions = [
    { value: "planned", label: "Planirano" },
    { value: "ready", label: "Spremno" },
    { value: "in_progress", label: "U tijeku" },
    { value: "completed", label: "Zavrseno" },
    { value: "on_hold", label: "Pauzirano" },
    { value: "skipped", label: "Preskoceno" },
    { value: "canceled", label: "Otkazano" },
  ];

  return (
    <div className="mes-page monitoring-dashboard">
      <Header title="MES - Pracenje proizvodnje" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Date Selector */}
          <MesDateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            showTimelineIndicators={true}
          />

          {/* Controls */}
          <div className="mes-section dashboard-controls-section">
            <div className="dashboard-controls-row">
              <div className="control-group auto-refresh-toggle">
                <label className="toggle-label">
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                  <span className="toggle-text">Auto-refresh (60s)</span>
                </label>
              </div>

              <button
                className={`btn-refresh ${loading ? 'loading' : ''}`}
                onClick={loadStatus}
                disabled={loading}
              >
                <svg className="refresh-icon" viewBox="0 0 24 24" width="18" height="18">
                  <path fill="currentColor" d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
                <span>{loading ? "Ucitavanje..." : "Osvjezi"}</span>
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          {status && (
            <div className="dashboard-summary-grid">
              <div className="summary-card total">
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                </div>
                <div className="card-content">
                  <div className="card-value">{status.totalBatches}</div>
                  <div className="card-label">Ukupno zamjesa</div>
                </div>
              </div>

              <div className="summary-card completed">
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <div className="card-content">
                  <div className="card-value">{status.completed}</div>
                  <div className="card-label">Zavrseno</div>
                </div>
                <div className="card-progress">
                  <div className="mini-progress" style={{ width: `${status.totalBatches ? (status.completed / status.totalBatches) * 100 : 0}%` }}></div>
                </div>
              </div>

              <div className="summary-card in-progress">
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                  </svg>
                </div>
                <div className="card-content">
                  <div className="card-value">{status.in_progress}</div>
                  <div className="card-label">U tijeku</div>
                </div>
              </div>

              <div className="summary-card pending">
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                  </svg>
                </div>
                <div className="card-content">
                  <div className="card-value">{status.planned + status.ready}</div>
                  <div className="card-label">Ceka na red</div>
                </div>
              </div>

              <div className="summary-card other">
                <div className="card-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </div>
                <div className="card-content">
                  <div className="card-value">{status.on_hold + status.skipped + status.canceled}</div>
                  <div className="card-label">Pauzirano / Otkazano</div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {status && status.totalBatches > 0 && (
            <div className="mes-section progress-section">
              <div className="progress-header">
                <span className="progress-title">Napredak proizvodnje</span>
                <span className="progress-percentage">{calculateProgress()}%</span>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${calculateProgress()}%` }}
                  >
                    <div className="progress-glow"></div>
                  </div>
                </div>
              </div>
              <div className="progress-stats">
                <span>{status.completed} od {status.totalBatches} zamjesa zavrseno</span>
              </div>
            </div>
          )}

          {/* Plan vs actual Gantt */}
          {timeline && plannedBatches.length > 0 && (
            <div className="mes-section gantt-comparison-section">
              <div className="gantt-comparison-controls">
                <h3>Usporedba plana i izvršenja</h3>
                <div className="gantt-layer-toggles">
                  <label className="toggle-label">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={showPlannedGantt}
                        onChange={(e) => setShowPlannedGantt(e.target.checked)}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <span className="toggle-text">Planirano</span>
                  </label>
                  <label className="toggle-label">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={showActualGantt}
                        onChange={(e) => setShowActualGantt(e.target.checked)}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <span className="toggle-text">
                      Stvarno ({countBatchesWithActual(processParams)} zamjesa)
                    </span>
                  </label>
                </div>
              </div>
              {!showPlannedGantt && !showActualGantt ? (
                <p className="empty-state">Uključite barem jedan sloj za prikaz.</p>
              ) : (
                <GanttComparisonChart
                  plannedBatches={plannedBatches}
                  actualBatches={actualBatches}
                  selectedDate={selectedDate}
                  showPlanned={showPlannedGantt}
                  showActual={showActualGantt}
                  groupBy={ganttGroupBy}
                  onGroupByChange={setGanttGroupBy}
                />
              )}
            </div>
          )}

          {/* Product Progress */}
          {status?.batches && status.batches.length > 0 && (
            <div className="mes-section">
              <h3>Napredak po proizvodu</h3>
              <div className="product-progress-grid">
                {getProductProgress().map((product) => {
                  const progressPct = product.total > 0 ? (product.completed / product.total) * 100 : 0;
                  return (
                    <div key={product.product_name} className="product-progress-card">
                      <div className="product-progress-header">
                        <span className="product-name">{product.product_name}</span>
                        <span className="product-stats">
                          {product.completed}/{product.total} zamjesa
                        </span>
                      </div>
                      <div className="product-progress-bar">
                        <div
                          className="product-progress-fill"
                          style={{ width: `${progressPct}%` }}
                        ></div>
                      </div>
                      <div className="product-progress-details">
                        <span className="detail-item">
                          <span className="detail-icon completed-icon"></span>
                          {product.completed} zavrseno
                        </span>
                        {product.in_progress > 0 && (
                          <span className="detail-item">
                            <span className="detail-icon in-progress-icon"></span>
                            {product.in_progress} u tijeku
                          </span>
                        )}
                        <span className="detail-item mass-detail">
                          {Number(product.totalMassKg || 0).toFixed(0)} kg
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Batch List */}
          {status?.batches && status.batches.length > 0 && (
            <div className="mes-section">
              <h3>Zamjesi</h3>
              <div className="batch-table-container">
                <table className="mes-table dashboard-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>ID Zamjesa</th>
                      <th>Proizvod</th>
                      <th>Mikser</th>
                      <th>Status</th>
                      <th>Promijeni status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.batches.map((batch, index) => (
                      <tr key={batch.ID} className={`batch-row status-${batch.BatchStatus}`}>
                        <td className="batch-index">{index + 1}</td>
                        <td className="batch-id-cell">
                          <code>{batch.MixCode}</code>
                        </td>
                        <td className="batch-product">{batch.ProductName}</td>
                        <td className="batch-mixer">
                          <span className="mixer-badge">{batch.MixerName}</span>
                        </td>
                        <td>
                          <span className={`status-pill status-${batch.BatchStatus}`}>
                            <span className="status-dot"></span>
                            {getStatusLabel(batch.BatchStatus)}
                          </span>
                        </td>
                        <td className="action-cell">
                          <select
                            className="status-select"
                            value={batch.BatchStatus}
                            onChange={(e) => handleStatusChange(batch, e.target.value)}
                          >
                            {statusOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No Timeline Message */}
          {status && !status.hasTimeline && (
            <div className="mes-section empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" width="48" height="48">
                  <path fill="currentColor" d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
              </div>
              <p>Nema potvrdjenog timeline-a za {selectedDate}</p>
              <span className="empty-hint">Odaberite drugi datum ili kreirajte timeline u sekciji Planiranje</span>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default Dashboard;
